import { Injectable } from '@angular/core';
import { StorageService } from './storage.service';
import { Storage } from '@constants/storage';
import { IntercomService } from '@services/intercom.service';
import { BroadcastEvent } from '@interfaces/broadcast.interface';
import {
  ApiAuthLoginEmailResponse,
  ApiAuthLoginResponse,
} from '@interfaces/api.auth.interface';
import { ApiUserResponse } from '@interfaces/api.user.interface';
import { Router } from '@angular/router';
import { SessionService as SessionServiceNew } from '@arkius/shared-services';
import { NavController } from '@ionic/angular';
import { SignalrService } from '../../signalr.service';
import { SocialLogin } from '@capgo/capacitor-social-login';

export type LoginProvider = 'email' | 'google' | 'apple';

@Injectable()
export class SessionService {
  constructor(
    private navController: NavController,
    private intercom: IntercomService,
    private sessionServiceNew: SessionServiceNew,
    private signalr: SignalrService,
  ) {}

  async isLoggedIn(): Promise<any> {
    const token = this.sessionServiceNew.get(Storage.SESSION_TOKEN);
    if (token) {
      return Promise.resolve(token);
    } else {
      return Promise.reject(false);
    }
  }

  async setUserData(data: ApiUserResponse): Promise<boolean> {
    this.sessionServiceNew.set(Storage.USER_DATA, data);
    return Promise.resolve(true);
  }

  async getUserData(): Promise<ApiUserResponse | null> {
    return this.sessionServiceNew.get(Storage.USER_DATA);
  }

  async save(
    data: ApiAuthLoginResponse | ApiAuthLoginEmailResponse,
    provider?: LoginProvider,
  ): Promise<boolean> {
    this.sessionServiceNew.set(Storage.SESSION_TOKEN, data.token);
    this.sessionServiceNew.set(Storage.REFRESH_TOKEN, data.refreshToken);
    if (provider) {
      this.sessionServiceNew.set(Storage.LOGIN_PROVIDER, provider);
    }
    this.intercom.broadcast({
      event: BroadcastEvent.USER_LOGGED_IN,
    });
    return Promise.resolve(true);
  }
  async clearSession(): Promise<boolean> {
    this.sessionServiceNew.remove(Storage.SESSION_TOKEN);
    this.sessionServiceNew.remove(Storage.REFRESH_TOKEN);
    return Promise.resolve(true);
  }

  async logout(): Promise<boolean> {
    const provider = this.sessionServiceNew.get(
      Storage.LOGIN_PROVIDER,
    ) as LoginProvider | null;

    await this.clearSession();
    if (provider === 'google' || provider === 'apple') {
      await this.clearSocialProviderSession(provider);
    } else if (!provider) {
      // Existing installations may not have a stored provider yet.
      await this.clearSocialProviderSession('google');
      await this.clearSocialProviderSession('apple');
    }
    this.sessionServiceNew.remove(Storage.LOGIN_PROVIDER);
    this.intercom.broadcast({
      event: BroadcastEvent.USER_LOGGED_OUT,
    });
    this.signalr.unregisterUser();
    this.navController.navigateRoot(['auth/login']);

    return true;
  }

  private async clearSocialProviderSession(
    provider: 'google' | 'apple',
  ): Promise<void> {
    try {
      await SocialLogin.logout({ provider });
    } catch (error) {
      // A normal email/password session may not have an initialized provider
      // session. That must not prevent the application logout from completing.
      console.warn(
        `[${provider} Sign-In] Could not clear provider session.`,
        error,
      );
    }
  }

  async logoutAdmin(): Promise<boolean> {
    await this.clearSession();
    this.sessionServiceNew.remove(Storage.LOGIN_PROVIDER);
    this.intercom.broadcast({
      event: BroadcastEvent.USER_LOGGED_OUT,
    });
    this.navController.navigateRoot(['login']);

    return true;
  }
}
